import { useState } from 'react';
import PropTypes from 'prop-types';
import { PiFunnelFill } from 'react-icons/pi';

import { cn } from '#/lib/utils';
import { Button } from '#/modules/commonComponents/button';
import { Checkbox } from '#/modules/commonComponents/checkbox';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '#/modules/commonComponents/popover';
import {
  applyQtyFilters,
  BASE_QTY_GT_ZERO,
  formatQtyFilterSummary,
  isBaseQtyGtActive,
} from '#/modules/commonComponents/table/qtyFilterHelpers';
import { toast } from '#/utils/toast';

const EMPTY_META = {};

/**
 * 表頭數量篩選 Popover：>0／min／max（server filter）。
 * meta.showQtyGt=false 時只留 min／max（金額區間共用，見 createAmountRangeFilterMeta）。
 */
function ColumnFilterQtyPopover({ column }) {
  const meta = column.columnDef.meta ?? EMPTY_META;
  const [open, setOpen] = useState(false);
  const showQtyGt = meta.showQtyGt !== false;
  const minLabel = meta.rangeMinLabel ?? '最小數量';
  const maxLabel = meta.rangeMaxLabel ?? '最大數量';
  const rangeErrorMessage = meta.rangeErrorMessage ?? '最小數量不可大於最大數量';

  const externalGt = meta.qtyGt;
  const externalMin = meta.qtyMin ?? '';
  const externalMax = meta.qtyMax ?? '';

  const [prevKey, setPrevKey] = useState(`${externalGt}|${externalMin}|${externalMax}`);
  const [localGt, setLocalGt] = useState(isBaseQtyGtActive(externalGt));
  const [localMin, setLocalMin] = useState(externalMin === undefined || externalMin === null
    ? ''
    : String(externalMin));
  const [localMax, setLocalMax] = useState(externalMax === undefined || externalMax === null
    ? ''
    : String(externalMax));

  const nextKey = `${externalGt}|${externalMin}|${externalMax}`;
  if (nextKey !== prevKey) {
    setPrevKey(nextKey);
    setLocalGt(isBaseQtyGtActive(externalGt));
    setLocalMin(externalMin === undefined || externalMin === null ? '' : String(externalMin));
    setLocalMax(externalMax === undefined || externalMax === null ? '' : String(externalMax));
  }

  const hasMin = localMin !== '';
  const gtDisabled = hasMin || !showQtyGt;
  const summary = formatQtyFilterSummary({
    baseQtyGt: localGt && !gtDisabled ? BASE_QTY_GT_ZERO : undefined,
    baseQtyMin: localMin,
    baseQtyMax: localMax,
  });
  const hasActiveFilter = Boolean(formatQtyFilterSummary({
    baseQtyGt: externalGt,
    baseQtyMin: externalMin,
    baseQtyMax: externalMax,
  }));

  function commit(next) {
    meta.onServerQtyFilterChange?.(next);
  }

  function handleApply() {
    const draft = {
      baseQtyGt: localGt && !gtDisabled ? BASE_QTY_GT_ZERO : undefined,
      baseQtyMin: localMin,
      baseQtyMax: localMax,
    };
    const result = applyQtyFilters({}, draft);
    if (!result.ok) {
      toast.error(rangeErrorMessage);
      return;
    }
    commit(draft);
    setOpen(false);
  }

  function handleClear(event) {
    event?.stopPropagation?.();
    setLocalGt(false);
    setLocalMin('');
    setLocalMax('');
    commit({
      baseQtyGt: undefined,
      baseQtyMin: undefined,
      baseQtyMax: undefined,
    });
    setOpen(false);
  }

  function handleGtChange(checked) {
    if (gtDisabled) {
      return;
    }
    setLocalGt(Boolean(checked));
  }

  function handleMinChange(event) {
    const value = event.target.value;
    setLocalMin(value);
    if (value !== '') {
      setLocalGt(false);
    }
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className={cn(
            'h-7 max-w-full gap-1 px-2 text-xs font-normal',
            hasActiveFilter && 'border-slate-400 bg-slate-100 text-slate-700',
          )}
        >
          <PiFunnelFill className="size-3 shrink-0" />
          <span className="truncate">{summary || '全部'}</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-64 p-3"
        align="start"
        onOpenAutoFocus={(event) => event.preventDefault()}
      >
        <div className="space-y-3">
          {showQtyGt ? (
            <label className={cn(
              'flex items-center gap-2 text-sm',
              gtDisabled && 'cursor-not-allowed opacity-50',
            )}
            >
              <Checkbox
                checked={localGt && !gtDisabled}
                disabled={gtDisabled}
                onCheckedChange={handleGtChange}
              />
              <span>&gt; 0（排除 0 與負數）</span>
            </label>
          ) : null}

          <div className="grid grid-cols-2 gap-2">
            <label className="block space-y-1 text-xs text-muted-foreground">
              <span>{minLabel}</span>
              <input
                type="number"
                inputMode="decimal"
                value={localMin}
                onChange={handleMinChange}
                className={cn(
                  'h-8 w-full rounded-md border border-input bg-transparent px-2 text-sm',
                  'outline-none focus-visible:border-ring focus-visible:ring-2 focus-visible:ring-ring/40',
                )}
                placeholder="≥"
              />
            </label>
            <label className="block space-y-1 text-xs text-muted-foreground">
              <span>{maxLabel}</span>
              <input
                type="number"
                inputMode="decimal"
                value={localMax}
                onChange={(event) => setLocalMax(event.target.value)}
                className={cn(
                  'h-8 w-full rounded-md border border-input bg-transparent px-2 text-sm',
                  'outline-none focus-visible:border-ring focus-visible:ring-2 focus-visible:ring-ring/40',
                )}
                placeholder="≤"
              />
            </label>
          </div>

          {showQtyGt && gtDisabled ? (
            <p className="text-xs text-muted-foreground">已填最小數量時不可勾選「&gt; 0」</p>
          ) : null}

          <div className="flex justify-end gap-2">
            <Button type="button" variant="ghost" size="sm" onClick={handleClear}>
              清除
            </Button>
            <Button type="button" size="sm" onClick={handleApply}>
              套用
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}

ColumnFilterQtyPopover.propTypes = {
  column: PropTypes.shape({
    columnDef: PropTypes.shape({
      meta: PropTypes.object,
    }),
  }).isRequired,
};

export default ColumnFilterQtyPopover;
